// Product configuration for the dynamic form.
// Edit this file to list your subscription products (with term lookup keys)
// and one-time addons. Use lookup keys or price ids that your backend understands.
//
// ⚠️  IMPORTANT: Product IDs must remain stable for shared URLs to work!
// ⚠️  Changing product IDs will break existing shared configuration URLs.
// ⚠️  If you need to change Stripe products, update the stripeProductId instead.

// Example shape:
// [
//   {
//     id: 'business-plus', // local id used in the UI
//     name: 'Business +',
//     stripeProductId: 'prod_HSDo46rWBPNFLT',
//     terms: [
//       { id: 'yearly', label: 'Yearly', lookup_key: 'business_plus_yearly' },
//       { id: 'monthly', label: 'Monthly', lookup_key: 'business_plus_monthly' }
//     ],
//     addons: [
//       { id: 'problogger', label: 'Pro Blogger', monthly_lookup_key: 'problogger_monthly', yearly_lookup_key: 'problogger_yearly', availableFor: [{ term: 'monthly' }, { term: 'yearly' }] }
//     ]
//   }
// ]

// Primary subscription plans - user selects ONE
const primaryProducts = [
  {
    id: 'starter-plan',
    name: 'Starter Plan',
    stripeProductId: 'prod_TClmFQSmv8buKH',
    // Optional visibility controls (omit to show for all terms)
    // availableTerms: ['monthly','yearly'],
    terms: [
      { id: 'yearly', label: 'Yearly', lookup_key: 'starter_plan_yearly', price_display: 499.00 },
      { id: 'monthly', label: 'Monthly', lookup_key: 'starter_plan_monthly', price_display: 49.00 }
    ]
  },
  {
    id: 'business-plan',
    name: 'Business Plan',
    stripeProductId: 'prod_SPN2y4fgBBwJkN',
    // Optional visibility controls (omit to show for all terms)
    // availableTerms: ['monthly','yearly'],
    terms: [
      { id: 'yearly', label: 'Yearly', lookup_key: 'business_plan_yearly', price_display: 1118.00 },
      { id: 'monthly', label: 'Monthly', lookup_key: 'business_plan_monthly', price_display: 99.00 }
    ]
  }
];

// Add-on subscription plans - user can select MULTIPLE (same billing period as primary)
const addonSubscriptions = [
  {
    id: 'pro-blogger',
    name: 'Pro Blogger',
    description: 'Advanced blogging features and content management',
    stripeProductId: 'prod_G5fSMHLjTpfuxa',
    // Optional visibility controls (keep omitted to show for all)
    // availableTerms: ['monthly','yearly'],
    // includeForPrimary: ['business-plan'],
    // excludeForPrimary: ['starter-plan'],
    // visibilityRules: [
    //   { terms: ['monthly'], primary: ['business-plan'] },
    //   { terms: ['yearly'] }
    // ],
    // Test condition: only show on monthly for Business Plan
    visibilityRules: [{ terms: ['monthly'], primary: ['business-plan'] }],
    terms: [
      { id: 'yearly', label: 'Yearly', lookup_key: 'problogger_yearly', price_display: 60.00 },
      { id: 'monthly', label: 'Monthly', lookup_key: 'problogger_monthly', price_display: 5.00 }
    ]
  },
  {
    id: 'ecommerce-pro',
    name: 'E-commerce Pro',
    description: 'Advanced online store features and payment processing',
    stripeProductId: 'prod_TCoViqlTc6SWFo',
    // Optional visibility controls (examples):
    // availableTerms: ['monthly'],
    // includeForPrimary: ['starter-plan','business-plan'],
    // Test condition: hide for Starter Plan regardless of term
    excludeForPrimary: ['starter-plan'],
    terms: [
      { id: 'yearly', label: 'Yearly', lookup_key: 'ecommerce_pro_yearly', price_display: 120.00 },
      { id: 'monthly', label: 'Monthly', lookup_key: 'ecommerce_pro_monthly', price_display: 12.00 }
    ]
  },
  {
    id: 'analytics-plus',
    name: 'Analytics Plus',
    description: 'Advanced analytics and reporting dashboard',
    stripeProductId: 'prod_TCoVhssd5XSKiW',
    // Optional visibility controls (examples):
    // availableTerms: ['yearly'],
    // excludeForPrimary: ['starter-plan'],
    // Test condition: only visible on yearly for any plan
    availableTerms: ['yearly'],
    terms: [
      { id: 'yearly', label: 'Yearly', lookup_key: 'analytics_plus_yearly', price_display: 84.00 },
      { id: 'monthly', label: 'Monthly', lookup_key: 'analytics_plus_monthly', price_display: 8.00 }
    ]
  }
];

// One-time charges that can be added to any subscription
const oneTimeProducts = [
  {
    id: 'stock-photos', // NOTE: id must stay stable -- shared URLs reference it.
    name: 'Stock Photos',
    description: 'One time stock photo purchase',
    // WAS 'setup_fee', which collided with setupFeeConfig.monthlySetupFee below. That was
    // harmless only while this section was disabled; once enabled, selecting Stock Photos
    // would have added 'setup_fee' while the server also appends it -- charging the
    // customer $99 twice, labelled as the wrong thing.
    // ⚠️  A Stripe price with lookup_key 'stock_photos_addon' must exist before the
    //     oneTimeServices feature flag is enabled. scripts/verify-prices.js checks this.
    lookup_key: 'stock_photos_addon',
    price_display: 99.00
    // Optional visibility controls (examples):
    // availableTerms: ['monthly','yearly'],
    // visibilityRules: [{ terms: ['yearly'] }],
    // includeForPrimary: ['business-plan']
  },
  {
    id: 'migration-service',
    name: 'Migration Service',
    description: 'Professional migration from your current platform',
    lookup_key: 'migration_service',
    price_display: 199.00,
    // Optional visibility controls (examples):
    // availableTerms: ['monthly'],
    // excludeForPrimary: ['starter-plan']
    // Test condition: only show for Business Plan on any term
    includeForPrimary: ['business-plan']
  },
  {
    id: 'custom-domain',
    name: 'Custom Domain Setup',
    description: 'Professional domain configuration and SSL setup',
    lookup_key: 'custom_domain_setup',
    price_display: 49.00
    // Optional visibility controls (examples):
    // visibilityRules: [{ primary: ['starter-plan','business-plan'] }]
  }
];

// Configuration for automatic setup fees
const setupFeeConfig = {
  enabled: true,
  monthlySetupFee: {
    lookup_key: 'setup_fee',
    price_display: 99.00,
    description: 'Setup fee (monthly subscriptions only)'
  }
};

/*
  Questions with conditional product addition.

  `icon` names a glyph in checkout/ui/Icon.jsx. It lives here rather than in a lookup table
  beside the component because the question and its subject belong together: a question added
  to this array without an icon should be an obvious omission at the point of editing, and
  questionInfo.test.jsx fails if one is missing.

  Which SECTION a question renders in is NOT declared here. It is derived: any answer carrying
  `addProduct` puts the question in section 3 ("Add to your package"), otherwise section 4
  ("Interested in growing further?"). See the partition in DynamicForm.jsx.
*/
const questions = [
  {
    id: 'photos-question',
    icon: 'camera',
    title: 'Do you have at least 20 photos for the website?',
    type: 'radio',
    answers: [
      {
        id: 'photos-yes',
        label: 'Yes, I have my own photos'
        // No product added
      },
      {
        id: 'photos-no-stock',
        label: 'No, add stock photos as needed',
        addProduct: {
          type: 'one-time',
          conditionalPricing: {
            'starter-plan': { lookup_key: 'stock_photos_essentials', price_display: 49.00 },
            'business-plan': { lookup_key: 'stock_photos_business', price_display: 98.00 }
          }
        }
      },
      {
        id: 'photos-no-basic',
        label: 'One image per page is fine'
        // No product added
      }
    ]
  },
  {
    id: 'logo-question',
    icon: 'shieldCheck',
    title: 'Do you need a logo?',
    type: 'radio',
    answers: [
      {
        id: 'logo-yes',
        label: 'Yes, design one for me',
        addProduct: {
          type: 'one-time',
          fixedProduct: {
            lookup_key: 'logo_design',
            price_display: 499.00
          }
        }
      },
      {
        id: 'logo-no',
        label: 'No, I have a logo'
        // No product added
      }
    ]
  },
  {
    id: 'seo-content-question',
    icon: 'document',
    title: 'Would you like us to write SEO-optimized content for your pages to give your site a better chance at ranking in search engines?',
    type: 'radio',
    answers: [
      {
        id: 'seo-content-yes',
        label: 'Yes, write my content',
        addProduct: {
          type: 'one-time',
          conditionalPricing: {
            'starter-plan': { lookup_key: 'seo_content_essentials', price_display: 999.00 },
            'business-plan': { lookup_key: 'seo_content_business', price_display: 1998.00 }
          }
        }
      },
      {
        id: 'seo-content-maybe',
        label: 'Maybe, tell me more first'
        // No product added
      },
      {
        id: 'seo-content-no',
        label: 'No thanks'
        // No product added
      }
    ]
  },
  {
    // NEW with the redesign. Placed before the SEO and ads questions because the mockups lead
    // section 4 with it. No answer carries addProduct, so it lands in section 4 and its rows
    // read "No charge" -- which is the literal truth: saying yes starts a conversation and a
    // scoped quote, and charges nothing today.
    id: 'accessibility-question',
    icon: 'accessibility',
    title: 'Would you like to learn more about ADA / WCAG accessibility enhancements for your site?',
    type: 'radio',
    answers: [
      {
        id: 'accessibility-yes',
        label: 'Yes, tell me more'
        // No product added - informational only
      },
      {
        id: 'accessibility-no',
        label: 'No thanks'
        // No product added
      }
    ]
  },
  {
    id: 'ongoing-seo-question',
    icon: 'barChart',
    title: 'Are you interested in ongoing SEO to outrank your competition in search engines and map listings?',
    type: 'radio',
    answers: [
      {
        id: 'ongoing-seo-yes',
        label: 'Yes, reach out to me'
        // No product added - informational only
      },
      {
        id: 'ongoing-seo-no',
        label: 'No thanks'
        // No product added
      }
    ]
  },
  {
    id: 'paid-ads-question',
    icon: 'speaker',
    title: 'Are you interested in paid advertising campaigns to make sure you are in front of your perfect customer?',
    type: 'radio',
    answers: [
      {
        id: 'paid-ads-yes',
        label: 'Yes, I would like to learn more'
        // No product added - informational only
      },
      {
        id: 'paid-ads-no',
        label: 'No thanks'
        // No product added
      }
    ]
  }
];

export default primaryProducts;
export { primaryProducts, addonSubscriptions, oneTimeProducts, setupFeeConfig, questions };
